import React, { useEffect, useRef, useState, useContext } from "react";
import styled from "styled-components";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import { ProfileContext } from "../contexts/ProfileContext";
import { LoadingSpinner } from "../components/loading-spinner";
import PastoralPagination from "../components/pastoral-care/components/pagination";
import OrganizationNameButton from "../components/organization-name-button";

ChartJS.register(ArcElement, Tooltip, Legend);

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const ResourcesPage = () => {
  const [activeTab, setActiveTab] = useState("digital");

  // Separate state for each tab - Changed to "ytd" by default
  const [digitalDataType, setDigitalDataType] = useState("ytd");
  const [physicalDataType, setPhysicalDataType] = useState("ytd");

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [exportOption, setExportOption] = useState("");

  // Separate date picker states for each tab
  const [showDigitalDatePicker, setShowDigitalDatePicker] = useState(false);
  const [showPhysicalDatePicker, setShowPhysicalDatePicker] = useState(false);

  // Separate temp date states for each tab - cleared by default
  const [tempDigitalStartDate, setTempDigitalStartDate] = useState("");
  const [tempDigitalEndDate, setTempDigitalEndDate] = useState("");
  const [tempPhysicalStartDate, setTempPhysicalStartDate] = useState("");
  const [tempPhysicalEndDate, setTempPhysicalEndDate] = useState("");

  // Separate range states for each tab
  const [selectedDigitalRange, setSelectedDigitalRange] = useState(null);
  const [selectedPhysicalRange, setSelectedPhysicalRange] = useState(null);

  const [digitalData, setDigitalData] = useState({});
  const [physicalData, setPhysicalData] = useState({});

  const [isDigitalLoading, setIsDigitalLoading] = useState(false);
  const [isPhysicalLoading, setIsPhysicalLoading] = useState(false);

  const [digitalPage, setDigitalPage] = useState(1);
  const [physicalPage, setPhysicalPage] = useState(1);
  const itemsPerPage = 10;

  const [isDigitalBreakdownOpen, setIsDigitalBreakdownOpen] = useState(true);
  const [isPhysicalBreakdownOpen, setIsPhysicalBreakdownOpen] = useState(true);

  const digitalSelectRef = useRef(null);
  const physicalSelectRef = useRef(null);
  const digitalDatePickerRef = useRef(null);
  const physicalDatePickerRef = useRef(null);
  const reportRef = useRef();

  const { getResourceReport, organizationNumber, worker, organization } =
    useContext(ProfileContext);

  // Get Year-to-Date (YTD) range - from Jan 1st to current date
  const getYTDRange = () => {
    const now = new Date();
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1); // January 1st of current year
    const startDate = firstDayOfYear.toISOString().split("T")[0];
    const endDate = now.toISOString().split("T")[0];
    return { start: startDate, end: endDate };
  };

  // Initialize with YTD as default on component mount
  useEffect(() => {
    const ytdRange = getYTDRange();

    // Set digital tab defaults - only selected range, not temp dates
    setSelectedDigitalRange(ytdRange);

    // Set physical tab defaults - only selected range, not temp dates
    setSelectedPhysicalRange(ytdRange);
  }, []);

  // Fetch Digital Data
  useEffect(() => {
    const fetchDigitalData = async () => {
      try {
        setIsDigitalLoading(true);
        const orgNumber = organizationNumber;
        let startDate = null;
        let endDate = null;

        if (digitalDataType === "custom" && selectedDigitalRange) {
          startDate = selectedDigitalRange.start;
          endDate = selectedDigitalRange.end;
        } else if (digitalDataType === "monthly") {
          const now = new Date();
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          startDate = firstDay.toISOString().split("T")[0];
          endDate = now.toISOString().split("T")[0];
        } else if (digitalDataType === "all") {
          // For "all" data, no date filtering
          startDate = null;
          endDate = null;
        } else if (digitalDataType === "ytd" && selectedDigitalRange) {
          // Handle YTD case
          startDate = selectedDigitalRange.start;
          endDate = selectedDigitalRange.end;
        } else if (selectedDigitalRange) {
          // Default case for YTD when dataType is not explicitly set
          startDate = selectedDigitalRange.start;
          endDate = selectedDigitalRange.end;
        }

        const response = await getResourceReport(
          orgNumber,
          "digital", // Pass type as "digital"
          startDate,
          endDate
        );

        if (response?.original) {
          await new Promise((resolve) => setTimeout(resolve, 800));
          setDigitalData(response.original?.resource_report || {});
        }
      } catch (error) {
        console.error("❌ Error fetching Digital Resource Report:", error);
      } finally {
        setIsDigitalLoading(false);
      }
    };

    fetchDigitalData();
  }, [digitalDataType, selectedDigitalRange, getResourceReport]);

  // Fetch Physical Data
  useEffect(() => {
    const fetchPhysicalData = async () => {
      try {
        setIsPhysicalLoading(true);
        const orgNumber = process.env.REACT_APP_TWILIO_NUMBER;
        let startDate = null;
        let endDate = null;

        if (physicalDataType === "custom" && selectedPhysicalRange) {
          startDate = selectedPhysicalRange.start;
          endDate = selectedPhysicalRange.end;
        } else if (physicalDataType === "monthly") {
          const now = new Date();
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          startDate = firstDay.toISOString().split("T")[0];
          endDate = now.toISOString().split("T")[0];
        } else if (physicalDataType === "all") {
          // For "all" data, no date filtering
          startDate = null;
          endDate = null;
        } else if (physicalDataType === "ytd" && selectedPhysicalRange) {
          // Handle YTD case
          startDate = selectedPhysicalRange.start;
          endDate = selectedPhysicalRange.end;
        } else if (selectedPhysicalRange) {
          // Default case for YTD when dataType is not explicitly set
          startDate = selectedPhysicalRange.start;
          endDate = selectedPhysicalRange.end;
        }

        const response = await getResourceReport(
          orgNumber,
          "physical", // Pass type as "physical"
          startDate,
          endDate
        );

        if (response?.original) {
          await new Promise((resolve) => setTimeout(resolve, 800));
          setPhysicalData(response.original?.resource_report || {});
        }
      } catch (error) {
        console.error("❌ Error fetching Physical Resource Report:", error);
      } finally {
        setIsPhysicalLoading(false);
      }
    };

    fetchPhysicalData();
  }, [physicalDataType, selectedPhysicalRange, getResourceReport]);

  // Dropdown logic for digital tab
  useEffect(() => {
    const selectElement = digitalSelectRef.current;
    if (!selectElement) return;
    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);
    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);
    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Dropdown logic for physical tab
  useEffect(() => {
    const selectElement = physicalSelectRef.current;
    if (!selectElement) return;
    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);
    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);
    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Date picker close outside click - Digital
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        digitalDatePickerRef.current &&
        !digitalDatePickerRef.current.contains(event.target)
      ) {
        setShowDigitalDatePicker(false);
      }
    };
    if (showDigitalDatePicker)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDigitalDatePicker]);

  // Date picker close outside click - Physical
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        physicalDatePickerRef.current &&
        !physicalDatePickerRef.current.contains(event.target)
      ) {
        setShowPhysicalDatePicker(false);
      }
    };
    if (showPhysicalDatePicker)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPhysicalDatePicker]);

  // Print handler
  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: `Resources_Report_${activeTab}_${
      new Date().toISOString().split("T")[0]
    }`,
    onAfterPrint: () => setExportOption(""),
  });

  // Excel Export handler for digital tab
  const handleExportExcelDigital = () => {
    const rows = Object.entries(digitalData || {}).map(([key, val]) => ({
      Resource: key,
      Count: val,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Digital Resources");
    XLSX.writeFile(
      workbook,
      `Digital_Resources_Report_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    setExportOption("");
  };

  // Excel Export handler for physical tab
  const handleExportExcelPhysical = () => {
    const rows = Object.entries(physicalData || {}).map(([key, val]) => ({
      Resource: key,
      Count: val,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Physical Resources");
    XLSX.writeFile(
      workbook,
      `Physical_Resources_Report_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    setExportOption("");
  };

  // Trigger export actions based on active tab
  useEffect(() => {
    if (exportOption === "print") {
      handlePrint();
    } else if (exportOption === "export") {
      if (activeTab === "digital") {
        handleExportExcelDigital();
      } else if (activeTab === "physical") {
        handleExportExcelPhysical();
      }
    }
  }, [exportOption, activeTab]);

  const colors = [
    "#EC3310",
    "#106271",
    "#64868D",
    "#DE735E",
    "#00323B",
    "#FFB0A2",
    "#FCD5C9",
    "#D9EEF0",
    "#2DB3C0",
    "#E29878",
    "#ADCFD4",
    "#DDC2B7",
    "#77B3B8",
    "#FFB0A2",
    "#FF7234",
    "#BFE9EE",
    "#178B96",
    "#2DB3C0",
    "#FFEAE4",
    "#77B3B8",
    "#8DDCE8",
    "#FFB0A2",
    "#E8B1A7",
    "#00323B",
    "#E4572E",
    "#76B041",
    "#F2A104",
    "#6699CC",
    "#D7263D",
    "#3B8EA5",
    "#F45D01",
    "#2E8B57",
    "#F28E2B",
    "#4E79A7",
    "#59A14F",
    "#E15759",
    "#B07AA1",
    "#9C755F",
    "#76B7B2",
    "#FF9F1C",
    "#2EC4B6",
    "#E71D36",
    "#3A86FF",
    "#8338EC",
    "#FB5607",
    "#FF006E",
    "#FFD166",
    "#06D6A0",
    "#118AB2",
    "#EF476F",
    "#073B4C",
    "#8AC926",
    "#FFCA3A",
    "#1982C4",
  ];

  const getChartData = (dataObj) => {
    const labels = Object.keys(dataObj || {});
    const values = Object.values(dataObj || {});
    return {
      labels,
      datasets: [
        {
          data: values.length ? values : [1],
          backgroundColor: labels.length
            ? colors.slice(0, labels.length)
            : "#E0E0E0",
          borderWidth: 0,
        },
      ],
    };
  };

  const chartOptions = {
    cutout: "55%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#333",
        bodyFont: { family: "Futura PT" },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  // Pagination logic
  const digitalEntries = Object.entries(digitalData || {});
  const physicalEntries = Object.entries(physicalData || {});

  console.log(digitalEntries?.length);

  const paginatedDigitalData = digitalEntries.slice(
    (digitalPage - 1) * itemsPerPage,
    digitalPage * itemsPerPage
  );

  const paginatedPhysicalData = physicalEntries.slice(
    (physicalPage - 1) * itemsPerPage,
    physicalPage * itemsPerPage
  );

  const calculateTotal = (dataObj) => {
    return Object.values(dataObj || {}).reduce((sum, val) => sum + val, 0);
  };

  // Handle Year to Date click for digital tab
  const handleDigitalYTD = () => {
    const ytdRange = getYTDRange();
    setDigitalDataType("ytd");
    setSelectedDigitalRange(ytdRange);
    setShowDigitalDatePicker(false);
  };

  // Handle Year to Date click for physical tab
  const handlePhysicalYTD = () => {
    const ytdRange = getYTDRange();
    setPhysicalDataType("ytd");
    setSelectedPhysicalRange(ytdRange);
    setShowPhysicalDatePicker(false);
  };

  // Handle cancel for digital tab - reset to empty dates
  const handleDigitalCancel = () => {
    setTempDigitalStartDate("");
    setTempDigitalEndDate("");
    setShowDigitalDatePicker(false);
    // Don't change dataType, let user choose another filter
  };

  // Handle cancel for physical tab - reset to empty dates
  const handlePhysicalCancel = () => {
    setTempPhysicalStartDate("");
    setTempPhysicalEndDate("");
    setShowPhysicalDatePicker(false);
    // Don't change dataType, let user choose another filter
  };

  return (
    <Wrapper>
      <TopestSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? (
          ""
        ) : (
          <TopSection>
            <OrganizationNameButton />
          </TopSection>
        )}

        <TitleText>Resource</TitleText>
      </TopestSection>

      <TabContainer>
        <Tab
          active={activeTab === "digital"}
          onClick={() => setActiveTab("digital")}
        >
          Digital Resources
        </Tab>
        {worker && worker.role !== "subscriber" && (
          <Tab
            active={activeTab === "physical"}
            onClick={() => setActiveTab("physical")}
          >
            Physical Resources
          </Tab>
        )}
      </TabContainer>

      <div>
        {/* DIGITAL TAB CONTENT */}
        {activeTab === "digital" && (
          <TabContent>
            <TopSection>
              <FilterSection>
                <ToggleButtonContainer>
                  <ToggleButtonGroup>
                    <ToggleButton
                      active={digitalDataType === "ytd"}
                      onClick={handleDigitalYTD}
                    >
                      YEAR TO DATE
                    </ToggleButton>

                    <ToggleButton
                      active={digitalDataType === "all"}
                      onClick={() => {
                        setDigitalDataType("all");
                        setShowDigitalDatePicker(false);
                      }}
                    >
                      ALL DATA
                    </ToggleButton>

                    <ToggleButton
                      active={digitalDataType === "monthly"}
                      onClick={() => {
                        setDigitalDataType("monthly");
                        setShowDigitalDatePicker(false);
                      }}
                    >
                      CURRENT MONTH
                    </ToggleButton>
                    <ToggleButton
                      active={digitalDataType === "custom"}
                      onClick={() => setShowDigitalDatePicker(true)}
                    >
                      CUSTOM DATE RANGE
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {showDigitalDatePicker && (
                    <DatePickerDropdown ref={digitalDatePickerRef}>
                      <DateInputGroup>
                        <label>From</label>
                        <input
                          type="date"
                          value={tempDigitalStartDate}
                          onChange={(e) =>
                            setTempDigitalStartDate(e.target.value)
                          }
                        />
                      </DateInputGroup>
                      <DateInputGroup>
                        <label>To</label>
                        <input
                          type="date"
                          value={tempDigitalEndDate}
                          onChange={(e) =>
                            setTempDigitalEndDate(e.target.value)
                          }
                        />
                      </DateInputGroup>
                      <DatePickerActions>
                        <button
                          onClick={() => {
                            if (!tempDigitalStartDate || !tempDigitalEndDate)
                              return alert("Select both dates.");
                            setSelectedDigitalRange({
                              start: tempDigitalStartDate,
                              end: tempDigitalEndDate,
                            });
                            setDigitalDataType("custom");
                            setShowDigitalDatePicker(false);
                          }}
                        >
                          Apply
                        </button>
                        <button onClick={handleDigitalCancel}>Cancel</button>
                      </DatePickerActions>
                    </DatePickerDropdown>
                  )}
                </ToggleButtonContainer>
              </FilterSection>

              {/* RIGHT SIDE CONTROLS */}
              <RightControlsSection>
                <ControlsColumn>
                  {/* Export Menu - First Row */}
                  <SelectWrapper>
                    <ExportSelect
                      ref={digitalSelectRef}
                      value={exportOption}
                      onChange={(e) => setExportOption(e.target.value)}
                    >
                      <option value="">Export Options</option>
                      <option value="print">Print PDF</option>
                      <option value="export">Export as Excel Sheet</option>
                    </ExportSelect>
                    <ArrowIcon isOpen={isSelectOpen}>
                      <ChevronDown />
                    </ArrowIcon>
                  </SelectWrapper>

                  {/* Total Resources - Second Row */}
                  <TotalDisplayWrapper>
                    <TotalDisplay>
                      <TotalLabel>Total Resources:</TotalLabel>
                      <TotalValue>
                        {isDigitalLoading ? "..." : calculateTotal(digitalData)}
                      </TotalValue>
                    </TotalDisplay>
                  </TotalDisplayWrapper>
                </ControlsColumn>
              </RightControlsSection>
            </TopSection>

            <div ref={reportRef}>
              {/* ========== HIDDEN PDF-ONLY TITLE ========== */}
              <PdfOnlyHeader>
                <div className="main-title">DIGITAL RESOURCES</div>
              </PdfOnlyHeader>
              <ChartSection>
                <ChartContainer>
                  {isDigitalLoading ? (
                    <div className="spinner-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <Doughnut
                      data={getChartData(digitalData)}
                      options={chartOptions}
                    />
                  )}
                </ChartContainer>
              </ChartSection>
              <BreakdownCard isOpen={isDigitalBreakdownOpen}>
                <BreakdownTitle
                  onClick={() =>
                    setIsDigitalBreakdownOpen(!isDigitalBreakdownOpen)
                  }
                >
                  <ChevronIcon>
                    {isDigitalBreakdownOpen ? (
                      <ChevronDown />
                    ) : (
                      <ChevronRight />
                    )}
                  </ChevronIcon>
                  DIGITAL RESOURCES BREAKDOWN
                </BreakdownTitle>

                {isDigitalBreakdownOpen && (
                  <BreakdownContent>
                    {isDigitalLoading ? (
                      <div className="spinner-center">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        <TableContainer>
                          <StyledTable>
                            <thead>
                              <tr>
                                <th>Color</th>
                                <th>Resource Name</th>
                                <th>Total Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedDigitalData.length ? (
                                paginatedDigitalData.map(([key, val], i) => {
                                  const actualIndex =
                                    (digitalPage - 1) * itemsPerPage + i;
                                  return (
                                    <tr key={i}>
                                      <td>
                                        <ColorCircle
                                          color={colors[actualIndex]}
                                        />
                                      </td>
                                      <td>{key}</td>
                                      <td>{val}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="3">
                                    No digital resource data available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </StyledTable>
                        </TableContainer>

                        <StyledPagination>
                          <PastoralPagination
                            currentPage={digitalPage}
                            itemsPerPage={itemsPerPage}
                            total_count={digitalEntries.length}
                            handlePageChange={setDigitalPage}
                          />
                        </StyledPagination>
                      </>
                    )}
                  </BreakdownContent>
                )}
              </BreakdownCard>
            </div>
          </TabContent>
        )}

        {/* PHYSICAL TAB CONTENT */}
        {activeTab === "physical" && (
          <TabContent>
            <TopSection>
              <FilterSection>
                <ToggleButtonContainer>
                  <ToggleButtonGroup>
                    <ToggleButton
                      active={physicalDataType === "ytd"}
                      onClick={handlePhysicalYTD}
                    >
                      YEAR TO DATE
                    </ToggleButton>
                    <ToggleButton
                      active={physicalDataType === "all"}
                      onClick={() => {
                        setPhysicalDataType("all");
                        setShowPhysicalDatePicker(false);
                      }}
                    >
                      ALL DATA
                    </ToggleButton>
                    <ToggleButton
                      active={physicalDataType === "monthly"}
                      onClick={() => {
                        setPhysicalDataType("monthly");
                        setShowPhysicalDatePicker(false);
                      }}
                    >
                      CURRENT MONTH
                    </ToggleButton>
                    <ToggleButton
                      active={physicalDataType === "custom"}
                      onClick={() => setShowPhysicalDatePicker(true)}
                    >
                      CUSTOM DATE RANGE
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {showPhysicalDatePicker && (
                    <DatePickerDropdown ref={physicalDatePickerRef}>
                      <DateInputGroup>
                        <label>From</label>
                        <input
                          type="date"
                          value={tempPhysicalStartDate}
                          onChange={(e) =>
                            setTempPhysicalStartDate(e.target.value)
                          }
                        />
                      </DateInputGroup>
                      <DateInputGroup>
                        <label>To</label>
                        <input
                          type="date"
                          value={tempPhysicalEndDate}
                          onChange={(e) =>
                            setTempPhysicalEndDate(e.target.value)
                          }
                        />
                      </DateInputGroup>
                      <DatePickerActions>
                        <button
                          onClick={() => {
                            if (!tempPhysicalStartDate || !tempPhysicalEndDate)
                              return alert("Select both dates.");
                            setSelectedPhysicalRange({
                              start: tempPhysicalStartDate,
                              end: tempPhysicalEndDate,
                            });
                            setPhysicalDataType("custom");
                            setShowPhysicalDatePicker(false);
                          }}
                        >
                          Apply
                        </button>
                        <button onClick={handlePhysicalCancel}>Cancel</button>
                      </DatePickerActions>
                    </DatePickerDropdown>
                  )}
                </ToggleButtonContainer>
              </FilterSection>

              {/* RIGHT SIDE CONTROLS */}
              <RightControlsSection>
                <ControlsColumn>
                  {/* Export Menu - First Row */}
                  <SelectWrapper>
                    <ExportSelect
                      ref={physicalSelectRef}
                      value={exportOption}
                      onChange={(e) => setExportOption(e.target.value)}
                    >
                      <option value="">Export Options</option>
                      <option value="print">Print PDF</option>
                      <option value="export">Export as Excel Sheet</option>
                    </ExportSelect>
                    <ArrowIcon isOpen={isSelectOpen}>
                      <ChevronDown />
                    </ArrowIcon>
                  </SelectWrapper>

                  {/* Total Resources - Second Row */}
                  <TotalDisplayWrapper>
                    <TotalDisplay>
                      <TotalLabel>Total Resources:</TotalLabel>
                      <TotalValue>
                        {isPhysicalLoading
                          ? "..."
                          : calculateTotal(physicalData)}
                      </TotalValue>
                    </TotalDisplay>
                  </TotalDisplayWrapper>
                </ControlsColumn>
              </RightControlsSection>
            </TopSection>

            <div ref={reportRef}>
              {/* ========== HIDDEN PDF-ONLY TITLE ========== */}
              <PdfOnlyHeader>
                <div className="main-title">PHYSICAL RESOURCES</div>
              </PdfOnlyHeader>
              <ChartSection>
                <ChartContainer>
                  {isPhysicalLoading ? (
                    <div className="spinner-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <Doughnut
                      data={getChartData(physicalData)}
                      options={chartOptions}
                    />
                  )}
                </ChartContainer>
              </ChartSection>
              <BreakdownCard isOpen={isPhysicalBreakdownOpen}>
                <BreakdownTitle
                  onClick={() =>
                    setIsPhysicalBreakdownOpen(!isPhysicalBreakdownOpen)
                  }
                >
                  <ChevronIcon>
                    {isPhysicalBreakdownOpen ? (
                      <ChevronDown />
                    ) : (
                      <ChevronRight />
                    )}
                  </ChevronIcon>
                  PHYSICAL RESOURCES BREAKDOWN
                </BreakdownTitle>

                {isPhysicalBreakdownOpen && (
                  <BreakdownContent>
                    {isPhysicalLoading ? (
                      <div className="spinner-center">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        <TableContainer>
                          <StyledTable>
                            <thead>
                              <tr>
                                <th>Color</th>
                                <th>Resource Name</th>
                                <th>Total Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedPhysicalData.length ? (
                                paginatedPhysicalData.map(([key, val], i) => {
                                  const actualIndex =
                                    (physicalPage - 1) * itemsPerPage + i;
                                  return (
                                    <tr key={i}>
                                      <td>
                                        <ColorCircle
                                          color={colors[actualIndex]}
                                        />
                                      </td>
                                      <td>{key}</td>
                                      <td>{val}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="3">
                                    No physical resource data available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </StyledTable>
                        </TableContainer>

                        <StyledPagination>
                          <PastoralPagination
                            currentPage={physicalPage}
                            itemsPerPage={itemsPerPage}
                            total_count={physicalEntries.length}
                            handlePageChange={setPhysicalPage}
                          />
                        </StyledPagination>
                      </>
                    )}
                  </BreakdownContent>
                )}
              </BreakdownCard>
            </div>
          </TabContent>
        )}
      </div>
    </Wrapper>
  );
};

export default ResourcesPage;

// ================= Styled Components =================

const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 2rem 0;
  background-color: #f3f9fa;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding: 1.5rem 0;
  }

  @media (max-width: 768px) {
    padding: 1rem 0;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0;
  }

  .spinner-center {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }
`;

const TopestSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

// New container for right side controls
const RightControlsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 172px; /* Match ExportSelect width */

  @media (max-width: 768px) {
    align-items: stretch;
    width: 100%;
    min-width: auto;
  }
`;

// Column layout for Export menu and Total Resources
const ControlsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  @media (max-width: 768px) {
    align-items: stretch;
  }
`;

const TotalDisplayWrapper = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TotalDisplay = styled.div`
  display: flex;
  justify-content: space-around;
  background: #f9fafa;
  border: 1px solid #106271;
  border-radius: 8px;
  padding: 8px 10px;
  width: 100%;
  height: 32px;
  box-sizing: border-box;
  gap: 5px;

  @media (max-width: 768px) {
    height: 40px;
  }

  @media (max-width: 480px) {
    height: 36px;
  }
`;

const TotalLabel = styled.span`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #106271;
  line-height: 1.2;
`;

const TotalValue = styled.span`
  font-family: "Futura PT", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #2db3c0;
  line-height: 0.8;
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 100%;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 2rem;
  background: #adcfd4;
  padding: 4px;
  width: 100%;
`;

const Tab = styled.button`
  font-family: "Futura PT";
  font-size: 16px;
  font-weight: 600;
  padding: 12px 0;
  border: none;
  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "#FFFFFF" : "#EC3310")};
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width:50%

  &:hover {
    background: ${({ active }) =>
      active ? "#2DB3C0" : "rgba(255, 255, 255, 0.3)"};
    color: ${({ active }) => (active ? "#FFFFFF" : "#EC3310")};
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 10px 0;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 8px 0;
    letter-spacing: 0.5px;
  }
`;

const TabContent = styled.div`
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-start;
  margin-bottom: 0;

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 0;
  }
`;

const ToggleButtonContainer = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 62px;
  overflow: hidden;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 640px) {
    border-radius: 12px;
    flex-direction: column;
    background: transparent;
    gap: 0.5rem;
  }
`;

const ToggleButton = styled.button`
  width: auto;
  min-width: 164px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center;

  border: none;
  border-radius: 62px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#86CCDA")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 6px rgba(0, 0, 0, 0.15)" : "none"};

  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;

    font-size: 14px; /* Keep typography consistent */
    letter-spacing: 0.15em; /* Keep consistent */
    border-radius: 8px;
    margin: 2px 0;
  }

  @media (max-width: 480px) {
    height: 36px;

    font-size: 14px; /* Maintain required size */
    letter-spacing: 0.25em; /* Maintain required spacing */

    padding: 0 8px;
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 280px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 0;
    right: auto;
    min-width: 260px;
  }

  @media (max-width: 480px) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    max-width: 300px;
    z-index: 1000;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    font-family: "Space Grotesk";

    @media (max-width: 480px) {
      font-size: 12px;
    }
  }

  input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 13px;
    font-family: "Space Grotesk";

    &:focus {
      outline: none;
      border-color: #2db3c0;
      box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.1);
    }

    @media (max-width: 480px) {
      padding: 6px 8px;
      font-size: 12px;
    }
  }
`;

const DatePickerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;

  button {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: "Space Grotesk";
    font-weight: 600;
    border: none;

    &:first-child {
      background-color: #2db3c0;
      color: white;

      &:hover {
        background-color: #249aa5;
      }
    }

    &:last-child {
      background-color: #f3f4f6;
      color: #666;

      &:hover {
        background-color: #e5e7eb;
      }
    }

    @media (max-width: 480px) {
      padding: 6px 10px;
      font-size: 12px;
    }
  }
`;

const ChartSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;

  @media (max-width: 768px) {
    margin: 1.5rem 0;
  }
`;

const ChartContainer = styled.div`
  width: 400px;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  @media (max-width: 1024px) {
    width: 350px;
    height: 350px;
  }

  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
  }

  @media (max-width: 480px) {
    width: 250px;
    height: 250px;
  }
`;

const BreakdownCard = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  margin: 2rem 0;
  display: flex;
  flex-direction: column;
  padding: ${({ isOpen }) => (isOpen ? "30px" : "20px 30px")};
  font-family: "Space Grotesk", sans-serif;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: ${({ isOpen }) => (isOpen ? "20px" : "16px 20px")};
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: ${({ isOpen }) => (isOpen ? "16px" : "12px 16px")};
    border-radius: 12px;
  }
`;

const BreakdownTitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold */
  font-style: normal;
  line-height: 100%;
  color: #2db3c0;
  letter-spacing: 0.25em; /* 25% spacing */
  text-align: center;

  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #249aa5;
  }

  @media (max-width: 480px) {
    font-size: 14px; /* Keeping typography consistent */
    letter-spacing: 0.25em; /* Keeping consistent */
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BreakdownContent = styled.div`
  margin-top: 1.5rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  font-family: "Space Grotesk", sans-serif;
  border-collapse: collapse;
  background-color: #fff;

  th,
  td {
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
  }

  thead th {
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f2f6f7;
  }
  tbody tr:hover {
    background-color: #f9f9f9;
  }

  @media (max-width: 768px) {
    font-size: 13px;

    th,
    td {
      padding: 12px 10px;
    }
  }

  @media (max-width: 480px) {
    font-size: 12px;

    th,
    td {
      padding: 10px 8px;
    }
  }
`;

const ColorCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const StyledPagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 10px;

  @media (max-width: 480px) {
    padding-top: 8px;
  }
`;
